import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { api } from '../../api/client';

export default function UserPrivileges() {
  const { memberId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const member = location.state?.member;

  const [menus, setMenus] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchMenus();
  }, []);

  useEffect(() => {
    if (member?.permissions) {
      const initial = {};
      member.permissions.forEach((id) => {
        initial[id] = true;
      });
      setChecked(initial);
    }
  }, [member]);

  const fetchMenus = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/company/permissions');
      setMenus(res.data);
    } catch (err) {
      console.error('Failed to fetch permission menus:', err);
      setError(err.response?.data?.detail || 'Failed to load menus');
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = (menuId) => {
    setChecked((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const menuIds = Object.keys(checked)
        .filter((id) => checked[id])
        .map(Number);
      await api.patch(`/api/company/members/${memberId}/permissions`, { menu_ids: menuIds });
      setSuccess('Privileges updated successfully');
      setTimeout(() => navigate('/dashboard/users'), 1000);
    } catch (err) {
      console.error('Failed to save privileges:', err);
      setError(err.response?.data?.detail || 'Failed to save privileges');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/dashboard/users"
          className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline mb-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Users
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold">Privileges</h1>
        <p className="text-gray-600 mt-1">
          {member ? (
            <>Manage <span className="font-medium">{member.full_name || member.email}</span> access</>
          ) : (
            `Choose what this user can access (member #${memberId})`
          )}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : menus.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No menus found</div>
        ) : (
          <div className="divide-y">
            {menus.map((menu) => (
              <label
                key={menu.id}
                className="flex items-center gap-3 px-6 py-3.5 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={!!checked[menu.id]}
                  onChange={() => toggleMenu(menu.id)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-800">{menu.menu}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <button
          onClick={() => navigate('/dashboard/users')}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Privileges'}
        </button>
      </div>
    </div>
  );
}
